import { computed, onMounted } from 'vue';
import { registryLinkGroups } from 'config';
import useProxy from '~/composables/dumb/useProxy';
import { SET_REGISTRIES } from '#shared/types/actions';
import { logError } from '#shared/utils/Logger';
import { httpUpdateAPIHeaders } from '#shared/utils/registry';
import { goToUrl } from '#shared/utils/Url';
import useKillswitches from '~/composables/useKillswitches';
import useSignInClick from '~/composables/useSignInClick';
import { onPropertiesReady } from '~/events';

let isUpdateListener = false;

export function useRegistry(config = {}) {
  const envProps = useEnvProps();
  const isMcom = envProps.isMcom;
  const hostlink = envProps.hostlink;
  const navUser = useNavUser();
  const navRegistry = useNavRegistry();

  const { checkUpdates = false } = config;
  const orgId = isMcom ? 12 : 13;
  const proxy = useProxy(hostlink);
  const killswitches = useKillswitches();
  const isRegistryDropdown = computed(() => killswitches.value.registryDropdownEnabled);
  const defaultLinks = {
    redirectUrl: `${hostlink}/registry/manage`,
    signinUrl: `${hostlink}/account/signin`,
  };

  async function fetchRegistries() {
    if (!isRegistryDropdown.value) return;

    const { uid: userId, guid: userGUID, isSignedIn } = navUser.data;

    if (!isSignedIn) return;

    const isCallMade = sessionStorage.getItem('registryFetch');

    if (isCallMade) {
      const storedData = localStorage.getItem('userRegistries');
      if (storedData?.length && storedData !== 'undefined') {
        const registries = JSON.parse(storedData);
        navRegistry[SET_REGISTRIES](registries);
        return;
      }
    }

    onPropertiesReady(async () => {
      const apiUrl = '/regsvc/registry/api/v1/registry/association';
      const payload = { userId, userGUID, orgId };
      const params = {
        headers: await httpUpdateAPIHeaders(envProps, payload, killswitches.value),
      };

      try {
        const result = await $fetch(proxy(apiUrl), { method: 'POST', body: payload, ...params });
        if (!result) return;

        const { registryInfo } = result;
        localStorage.setItem('userRegistries', JSON.stringify(registryInfo));
        sessionStorage.setItem('registryFetch', true);
        navRegistry[SET_REGISTRIES](registryInfo);
      } catch (e) {
        logError(e?.message);
      }
    });
  }

  async function setDefaultRegistry(registryGUID) {
    onPropertiesReady(async () => {
      const payload = { registryGUID, orgId };
      const params = {
        headers: await httpUpdateAPIHeaders(envProps, payload, killswitches.value),
      };
      const apiUrl = '/regsvc/registry/api/v1/registry/defaultRegistry';

      try {
        const result = await $fetch(proxy(apiUrl), { method: 'PUT', body: payload, ...params });

        if (result) {
          goToUrl(defaultLinks.redirectUrl);
          return;
        }
      } catch (e) {
        logError(e?.message);
      }

      useSignInClick();
      sessionStorage.removeItem('registryFetch');
      goToUrl(defaultLinks.signinUrl);
    });
  }

  function setSignInRedirectParams() {
    useSignInClick({ redirectUrl: defaultLinks.redirectUrl });
  }

  onMounted(() => {
    if (checkUpdates && !isUpdateListener) {
      isUpdateListener = true;
      window.addEventListener('onRegistryUpdate', () => {
        sessionStorage.removeItem('registryFetch');
        fetchRegistries();
      });
    }
  });

  return {
    fetchRegistries,
    setDefaultRegistry,
    defaultLinks,
    setSignInRedirectParams,
  };
}

export function useRegistryDropdown() {
  const envProps = useEnvProps();
  const isMcom = envProps.isMcom;
  const navUser = useNavUser();
  const navRegistry = useNavRegistry();

  const title = isMcom ? 'Gift Registry' : 'REGISTRY';
  const isSignedIn = computed(() => !!navUser.data?.isSignedIn);
  const hasRegestries = computed(() => navRegistry?.hasRegestries);
  const occasions = computed(() => navRegistry?.occasions);
  const allowedOccasions = computed(() => navRegistry?.getAllowedOccasions);
  const linkGroups = computed(() =>
    registryLinkGroups.map((group) => {
      const filteredItemsAllowance = isSignedIn.value
        ? group.items.filter(({ checkAllowance }) => (checkAllowance ? allowedOccasions.value.includes(checkAllowance) : true))
        : group.items;

      return {
        ...group,
        items: filteredItemsAllowance,
      };
    }),
  );
  const showSignIn = computed(() => !isSignedIn.value);

  return {
    hasRegestries,
    showSignIn,
    occasions,
    linkGroups,
    title,
  };
}
